/**
 * "Ver como" con los DATOS de la persona en las lecturas del servidor (migraciones
 * 20261027120000_ver_como_lectura_efectiva y la de "ver como" global en lecturas).
 *
 * Mientras hay un impersonado publicado con `authUserId`, el `fetch` del cliente de Supabase
 * agrega el header `x-ver-como: <auth_user_id>` a las lecturas de PostgREST: toda RPC y todo
 * GET/HEAD a una tabla o vista. En BD, `ver_como_uid()` lo valida con la sesión REAL (puede
 * impersonar, persona activa, regla de Super Admin) y solo lo toma en transacciones de solo
 * lectura: los helpers de identidad y las policies responden como la persona, y los de permiso
 * exigen el de la sesión real Y el de la persona. Una escritura nunca toma el header, aunque lo
 * lleve (una RPC VOLATILE corre en una transacción que puede escribir).
 *
 * Quedan fuera las lecturas de la sesión real que sostienen la app (perfil, menús, permisos y el
 * selector de "Ver como"): con la identidad de la persona el admin perdería su propio menú o el
 * selector para dejar de impersonar. Una consulta puntual se excluye con `sinVerComo(builder)`.
 *
 * Funciones PURAS y un flag de módulo, como `solo-lectura`: sin React ni Supabase.
 */

export const HEADER_VER_COMO = 'x-ver-como';

/** Marca de una consulta que se lee siempre con la sesión real; no sale del navegador. */
export const HEADER_SIN_VER_COMO = 'x-sin-ver-como';

/** RPC de la sesión real: perfil, menús y la lista del selector de "Ver como". */
export const RPC_SIN_VER_COMO: readonly string[] = [
  'get_current_user_profile',
  'get_current_user_role',
  'get_current_user_persona_id',
  'get_user_role',
  'get_user_menus',
  'get_menus_bloqueados',
  'get_ver_como_personas',
];

/** Tablas de menús y permisos: el menú de la vista fiel lo arma el front con el rol de la persona. */
export const TABLAS_SIN_VER_COMO: readonly string[] = [
  'menus',
  'submenus',
  'submenus_permisos',
  'submenus_permisos_disponibles',
  'permisos',
  'roles',
  'user_roles',
];

let uidVerComo: string | null = null;

/** Lo llama `ImpersonationTargetProvider` cada vez que cambia el impersonado. */
export function setUidVerComo(uid: string | null | undefined): void {
  uidVerComo = uid?.trim() || null;
}

export function getUidVerComo(): string | null {
  return uidVerComo;
}

function rutaDe(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

/** ¿Esta petición es una lectura de PostgREST que debe llevar el header? Pura. */
export function esLecturaVerComo(url: string, metodo = 'GET'): boolean {
  const ruta = rutaDe(url);
  const rpc = ruta.match(/\/rest\/v1\/rpc\/([^/]+)$/);
  if (rpc) return !RPC_SIN_VER_COMO.includes(decodeURIComponent(rpc[1]));
  const tabla = ruta.match(/\/rest\/v1\/([^/]+)$/);
  if (!tabla) return false;
  const m = metodo.toUpperCase();
  if (m !== 'GET' && m !== 'HEAD') return false;
  return !TABLAS_SIN_VER_COMO.includes(decodeURIComponent(tabla[1]));
}

/** Excluye una consulta del "ver como": se lee con la sesión real. */
export function sinVerComo<T extends { setHeader(name: string, value: string): T }>(builder: T): T {
  return builder.setHeader(HEADER_SIN_VER_COMO, '1');
}

type Fetch = typeof fetch;

/** Envuelve un fetch: agrega el header a las lecturas mientras hay "ver como". */
export function crearFetchVerComo(opciones: { base?: Fetch; uid?: () => string | null } = {}): Fetch {
  const uidActual = opciones.uid ?? getUidVerComo;
  return (input, init) => {
    const base = opciones.base ?? globalThis.fetch;
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    const omitir = headers.has(HEADER_SIN_VER_COMO);
    if (omitir) headers.delete(HEADER_SIN_VER_COMO);
    const uid = uidActual();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const metodo = init?.method ?? (input instanceof Request ? input.method : 'GET');
    if (!uid || omitir || !esLecturaVerComo(url, metodo)) {
      return omitir ? base(input, { ...init, headers }) : base(input, init);
    }
    headers.set(HEADER_VER_COMO, uid);
    return base(input, { ...init, headers });
  };
}
